import { ExerciseStateMachine } from './ExerciseStateMachine';

export const BicepCurlState = {
  EXTENDED: 'EXTENDED',      
  CURLING: 'CURLING',        
  CONTRACTED: 'CONTRACTED',  
  EXTENDING: 'EXTENDING'     
};

const MOVEMENT_THRESHOLD = 0.5; 
const MIN_TIME_IN_POSITION = 200; 
const EXTENDED_THRESHOLD_RIGHT = 130;
const EXTENDED_THRESHOLD_LEFT = 110;
const CURLED_THRESHOLD_RIGHT = 100;
const CURLED_THRESHOLD_LEFT = 80;

export class BicepCurlFSM extends ExerciseStateMachine {
  constructor() {
    const config = {
      initialState: BicepCurlState.EXTENDED,
      initialStateData: {
        activeSide: 'right',
        curlAngle: 0,
        previousCurlAngle: 0,
        isMovingUp: false,
        previousWristPosition: { x: 0, y: 0, z: 0 },
        previousElbowPosition: { x: 0, y: 0, z: 0 },
        previousTimestamp: 0,
        maxCurlAngle: 0,
        lastExtendedTime: 0,
        lastCurledTime: 0,
        extensionScore: 100,
        contractionScore: 100,
        elbowStabilityScore: 100,
        repFormScore: 0,
        exerciseProgress: 0
      },
      initialFormScores: {
        overall: 100,
        extensionScore: 100,
        contractionScore: 100,
        elbowStabilityScore: 100
      },
      transitions: {
        [BicepCurlState.EXTENDED]: [
          {
            targetState: BicepCurlState.CURLING,
            condition: (metrics, stateData, timestamp) => 
              metrics.isMovingUp && !metrics.isExtendedPosition && metrics.curlAngle < (stateData.activeSide === 'right' ? 120 : 100),
            action: (fsm, metrics, timestamp) => {
              fsm.stateData.lastExtendedTime = timestamp;
            }
          }
        ],
        
        [BicepCurlState.CURLING]: [
          {
            targetState: BicepCurlState.CONTRACTED,
            condition: (metrics, stateData, timestamp) => 
              metrics.isCurledPosition && timestamp - stateData.lastExtendedTime > MIN_TIME_IN_POSITION,
            action: (fsm, metrics, timestamp) => {
              fsm.stateData.lastCurledTime = timestamp;
              fsm.stateData.maxCurlAngle = Math.min(fsm.stateData.maxCurlAngle || 90, metrics.curlAngle);
            },
            completesRepetition: true,
            calculateRepScore: (metrics, stateData) => {
              return Math.round(
                (metrics.extensionScore * 0.3) + 
                (metrics.contractionScore * 0.4) + 
                (metrics.elbowStabilityScore * 0.3)
              );
            }
          },
          {
            targetState: BicepCurlState.EXTENDED,
            condition: (metrics, stateData, timestamp) => 
              metrics.isExtendedPosition && !metrics.isMovingUp
          }
        ],
        
        [BicepCurlState.CONTRACTED]: [
          {
            targetState: BicepCurlState.EXTENDING,
            condition: (metrics, stateData, timestamp) => 
              !metrics.isMovingUp && !metrics.isCurledPosition
          },
          {
            targetState: BicepCurlState.EXTENDED,
            condition: (metrics, stateData, timestamp) => 
              metrics.isExtendedPosition && timestamp - stateData.lastCurledTime > MIN_TIME_IN_POSITION
          }
        ],
        
        [BicepCurlState.EXTENDING]: [
          {
            targetState: BicepCurlState.EXTENDED,
            condition: (metrics, stateData, timestamp) => 
              metrics.isExtendedPosition,
            action: (fsm, metrics, timestamp) => {
              fsm.stateData.lastExtendedTime = timestamp;
            }
          },
          {
            targetState: BicepCurlState.CURLING,
            condition: (metrics, stateData, timestamp) => 
              metrics.isMovingUp && !metrics.isExtendedPosition
          }
        ]
      }
    };
    
    super(config);
    
    this.constructor.initialConfig = {
      initialState: config.initialState,
      initialStateData: { ...config.initialStateData },
      initialFormScores: { ...config.initialFormScores }
    };
  }
  
  computeMetrics(landmarks, worldLandmarks, timestamp) {
    if (!worldLandmarks[11] || !worldLandmarks[12] || 
        !worldLandmarks[13] || !worldLandmarks[14] || 
        !worldLandmarks[15] || !worldLandmarks[16]) {
      return {};
    }
    
    const leftShoulder = worldLandmarks[11];
    const rightShoulder = worldLandmarks[12];
    const leftElbow = worldLandmarks[13];
    const rightElbow = worldLandmarks[14];
    const leftWrist = worldLandmarks[15];
    const rightWrist = worldLandmarks[16];
    
    const activeSide = this.stateData.activeSide || 'right';
    const activeShoulder = activeSide === 'right' ? rightShoulder : leftShoulder;
    const activeElbow = activeSide === 'right' ? rightElbow : leftElbow;
    const activeWrist = activeSide === 'right' ? rightWrist : leftWrist;

    // Calculate raw elbow angle
    const rawElbowAngle = this.calculateJointAngle(activeShoulder, activeElbow, activeWrist);

    // NEW: Apply Z-compression correction for front-facing camera
    // When user faces camera, forearm Z-depth is compressed making angles appear 10-15° larger
    // Apply empirically-determined correction factor
    const FORESHORTENING_CORRECTION = 0.92;  // Reduces angle by ~8%
    const elbowAngle = rawElbowAngle * FORESHORTENING_CORRECTION;

    let isMovingUp = false;
    if (this.stateData.previousTimestamp) {
      const angleDifference = elbowAngle - (this.stateData.curlAngle || elbowAngle);
      isMovingUp = angleDifference < -MOVEMENT_THRESHOLD;
    }

    const extendedThreshold = activeSide === 'right' ? EXTENDED_THRESHOLD_RIGHT : EXTENDED_THRESHOLD_LEFT;
    const curledThreshold = activeSide === 'right' ? CURLED_THRESHOLD_RIGHT : CURLED_THRESHOLD_LEFT;

    const isExtendedPosition = elbowAngle > extendedThreshold;
    const isCurledPosition = elbowAngle < curledThreshold;

    const extensionScore = Math.min(100, (elbowAngle / 170) * 100);

    const contractionScore = Math.min(100, 100 - ((elbowAngle / 90) * 100));
    
    let elbowStabilityScore = 100;
    if (this.stateData.previousElbowPosition) {
      const elbowMovement = Math.abs(this.stateData.previousElbowPosition.x - activeElbow.x);
      elbowStabilityScore = Math.max(0, 100 - (elbowMovement * 500));
    }
    
    const extendedAngle = activeSide === 'right' ? 170 : 160;
    const curledAngle = activeSide === 'right' ? 40 : 50;
    const range = extendedAngle - curledAngle;
    const exerciseProgress = Math.min(100, Math.max(0, 
      100 - ((elbowAngle - curledAngle) / range) * 100));
    
    return {
      activeSide,
      curlAngle: elbowAngle,
      previousWristPosition: activeWrist,
      previousElbowPosition: activeElbow,
      previousTimestamp: timestamp,
      
      isExtendedPosition,
      isCurledPosition,
      isMovingUp,
      
      extensionScore,
      contractionScore,
      elbowStabilityScore,
      exerciseProgress
    };
  }
  
  updateFormScores(metrics, transitioned) {
    if (!metrics.extensionScore) return;

    this.formScores.extensionScore = metrics.extensionScore;
    this.formScores.contractionScore = metrics.contractionScore;
    this.formScores.elbowStabilityScore = metrics.elbowStabilityScore;

    // ROM: Always show CURRENT/REAL-TIME ROM for live feedback
    this.formScores.rangeOfMotion = Math.round(metrics.rangeOfMotion || 0);

    const currentFormScore = Math.round(
      (metrics.extensionScore * 0.3) +
      (metrics.contractionScore * 0.4) +
      (metrics.elbowStabilityScore * 0.3)
    );

    if (this.stateData.repFormScore) {
      this.formScores.overall = Math.round(this.formScores.overall * 0.7 + this.stateData.repFormScore * 0.3);
      this.stateData.repFormScore = 0;  // Reset after use so next frame uses current metrics
    } else {
      this.formScores.overall = Math.round(this.formScores.overall * 0.7 + currentFormScore * 0.3);
    }
  }
  
  generateFormFeedback(metrics, currentState) {
    if (!metrics.curlAngle) return null;
    
    let feedback = `TRACKING ${metrics.activeSide.toUpperCase()} ARM`;
    
    if (metrics.elbowStabilityScore < 70 && 
        (currentState === BicepCurlState.CURLING || currentState === BicepCurlState.CONTRACTED)) {
      feedback = `${metrics.activeSide === 'left' ? 'L: ' : 'R: '}Keep your elbow in position`;
    }
    else if (currentState === BicepCurlState.EXTENDED && metrics.extensionScore < 85) {
      feedback = `${metrics.activeSide === 'left' ? 'L: ' : 'R: '}Extend arm fully at bottom`;
    }
    else if (currentState === BicepCurlState.CONTRACTED && metrics.contractionScore < 85) {
      feedback = `${metrics.activeSide === 'left' ? 'L: ' : 'R: '}Curl higher for full contraction`;
    }
    
    return feedback;
  }
  
  setActiveSide(side) {
    if (side === 'left' || side === 'right') {
      this.stateData.activeSide = side;
    }
  }
  
  calculateJointAngle(a, b, c) {
    const ba = {
      x: a.x - b.x,
      y: a.y - b.y,
      z: a.z - b.z
    };
    
    const bc = {
      x: c.x - b.x,
      y: c.y - b.y,
      z: c.z - b.z
    };
    
    const dotProduct = ba.x * bc.x + ba.y * bc.y + ba.z * bc.z;
    
    const baMagnitude = Math.sqrt(ba.x * ba.x + ba.y * ba.y + ba.z * ba.z);
    const bcMagnitude = Math.sqrt(bc.x * bc.x + bc.y * bc.y + bc.z * bc.z);
    
    const angleRadians = Math.acos(dotProduct / (baMagnitude * bcMagnitude));
    const angleDegrees = angleRadians * (180 / Math.PI);
    
    return angleDegrees;
  }
} 